import { Typography } from '@/components/atoms';
import type { TypographyVariant } from '@/components/atoms/Typography';
import { useUIColors } from '@/core/hooks';
import { WebAmenityIcon } from '@/modules/property-details/components/WebAmenityIcon';
import {
  useWebAmenities,
  type WebAmenity,
} from '@/modules/property-details/hooks/useWebAmenities';
import React from 'react';
import { StyleSheet, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';

const FILLER_KEYS = ['a', 'b', 'c'] as const;

export interface GuestAmenitiesGridProps {
  readonly amenities: readonly string[];
  /** Phone / tablet — equal-width columns when 2+. */
  readonly isCompact: boolean;
  /** Phone uses 2 cols; tablet compact uses 3. */
  readonly isPhone: boolean;
  readonly labelVariant?: TypographyVariant;
}

/**
 * Desktop: start-aligned fixed cells (mirrors in RTL).
 * Compact (2+): equal-width columns (2 on phone, 3 on tablet); incomplete
 * last rows keep column width via invisible fillers.
 * Single amenity: always start-aligned (all breakpoints).
 */
export function GuestAmenitiesGrid({
  amenities,
  isCompact,
  isPhone,
  labelVariant = 'body',
}: Readonly<GuestAmenitiesGridProps>) {
  const colors = useUIColors();
  const amenityItems = useWebAmenities(amenities);
  const amenitiesPerRow = isPhone ? 2 : 3;
  const iconSize = isPhone ? 20 : 24;

  const renderAmenity = (
    amenity: WebAmenity,
    itemStyle: StyleProp<ViewStyle>,
    textStyle: StyleProp<TextStyle>,
  ) => {
    return (
      <View key={amenity.value} style={itemStyle}>
        <WebAmenityIcon
          iconUrl={amenity.iconUrl}
          label={amenity.label}
          size={iconSize}
          defaultColor={colors.fontPrimary}
        />
        <Typography
          variant={labelVariant}
          color="secondary"
          style={textStyle}
        >
          {amenity.label}
        </Typography>
      </View>
    );
  };

  /** One amenity — hug content, start edge (mirrors in RTL). */
  if (amenityItems.length === 1) {
    const only = amenityItems[0];
    if (!only) {
      return null;
    }
    return (
      <View style={styles.rowDesktop}>
        {renderAmenity(only, styles.itemStart, styles.amenityText)}
      </View>
    );
  }

  if (!isCompact) {
    return (
      <View style={styles.rowDesktop}>
        {amenityItems.map((amenity) =>
          renderAmenity(amenity, styles.itemDesktop, styles.amenityText),
        )}
      </View>
    );
  }

  const rows: React.JSX.Element[] = [];
  for (let start = 0; start < amenityItems.length; start += amenitiesPerRow) {
    const rowAmenities = amenityItems.slice(start, start + amenitiesPerRow);
    const rowKey = rowAmenities[0]?.value ?? `row-${start}`;
    const fillerCount = amenitiesPerRow - rowAmenities.length;

    rows.push(
      <View key={rowKey} style={styles.rowCompact}>
        {rowAmenities.map((amenity) =>
          renderAmenity(amenity, styles.itemCompact, styles.amenityTextCompact),
        )}
        {FILLER_KEYS.slice(0, fillerCount).map((fillerKey) => (
          <View
            key={`filler-${rowKey}-${fillerKey}`}
            style={styles.itemCompact}
          />
        ))}
      </View>,
    );
  }

  return <View style={styles.gridCompact}>{rows}</View>;
}

const styles = StyleSheet.create({
  rowDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
    justifyContent: 'flex-start',
  },
  itemDesktop: {
    alignItems: 'center',
    gap: 8,
    flexGrow: 0,
    flexShrink: 0,
    width: 104,
  },
  /** Single amenity: content width, pinned to start. */
  itemStart: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    flexGrow: 0,
    flexShrink: 0,
  },
  gridCompact: {
    width: '100%',
    gap: 16,
  },
  rowCompact: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  itemCompact: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    paddingHorizontal: 4,
  },
  amenityText: {
    textAlign: 'center',
    lineHeight: 20,
  },
  amenityTextCompact: {
    textAlign: 'center',
    lineHeight: 18,
  },
});
export default GuestAmenitiesGrid;
